/**
 * Store do estado de conexão em tempo real do ChatContar.
 *
 * Guarda o status, quantas tentativas seguidas falharam, desde quando a
 * conexão está caída e a frase do último resultado. Toda reconexão passa por
 * `requestChatReconnect`, que impõe um limite de tempo — o alerta nunca fica
 * preso em "Reconectando…" sem dizer no que deu.
 */

export type ChatConnStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  /** Sessão inválida: reconectar não resolve, só entrar de novo. */
  | 'expired';

/** O que o handler de reconexão devolve. */
export type ChatReconnectResult = 'connected' | 'expired' | 'failed';

export type ChatReconnectHandler = () => Promise<ChatReconnectResult>;

export interface ChatConnState {
  status: ChatConnStatus;
  /** Tentativas seguidas sem sucesso; zera ao conectar. */
  attempts: number;
  /** Momento (ms) em que caiu, ou null se nunca caiu nesta sessão. */
  since: number | null;
  /** Frase curta com o resultado da última tentativa. */
  message: string | null;
}

/** Passando disso o app para de tentar sozinho e passa a pedir ação. */
export const MAX_AUTO_ATTEMPTS = 4;

/** Tentativa mais lenta que isso vira fracasso — evita spinner eterno. */
export const RECONNECT_TIMEOUT_MS = 15_000;

const ESTADO_INICIAL: ChatConnState = {
  status: 'connecting',
  attempts: 0,
  since: null,
  message: null,
};

let state: ChatConnState = ESTADO_INICIAL;
const listeners = new Set<(s: ChatConnState) => void>();
let reconnectHandler: ChatReconnectHandler | null = null;
let emAndamento: Promise<ChatReconnectResult> | null = null;

function patch(p: Partial<ChatConnState>) {
  const next: ChatConnState = { ...state, ...p };
  if (
    next.status === state.status &&
    next.attempts === state.attempts &&
    next.since === state.since &&
    next.message === state.message
  ) {
    return;
  }
  state = next;
  listeners.forEach((l) => l(state));
}

export function getChatConn(): ChatConnState {
  return state;
}

export function getChatConnStatus(): ChatConnStatus {
  return state.status;
}

/** Marca o status vindo do canal (conectou, caiu sozinho, está subindo). */
export function setChatConnStatus(next: ChatConnStatus, message: string | null = null) {
  if (next === 'connected') {
    patch({ status: 'connected', attempts: 0, since: null, message: null });
    return;
  }
  const caiu = next === 'disconnected' || next === 'expired';
  patch({
    status: next,
    message,
    since: caiu ? state.since ?? Date.now() : state.since,
  });
}

export function subscribeChatConn(l: (s: ChatConnState) => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function registerChatReconnect(fn: ChatReconnectHandler | null) {
  reconnectHandler = fn;
}

/** Volta ao estado de boot — usado no logout e nos testes. */
export function resetChatConn() {
  state = ESTADO_INICIAL;
  emAndamento = null;
  listeners.forEach((l) => l(state));
}

function aplicarResultado(res: ChatReconnectResult) {
  if (res === 'connected') {
    patch({ status: 'connected', attempts: 0, since: null, message: null });
    return;
  }

  const attempts = state.attempts + 1;
  const since = state.since ?? Date.now();

  if (res === 'expired') {
    patch({
      status: 'expired',
      attempts,
      since,
      message: 'Sua sessão expirou. Entre novamente para voltar a receber mensagens.',
    });
    return;
  }

  patch({
    status: 'disconnected',
    attempts,
    since,
    message:
      attempts >= MAX_AUTO_ATTEMPTS
        ? `Não deu para reconectar em ${attempts} tentativas. Se a sua internet está funcionando, entre novamente no ChatContar.`
        : `A tentativa ${attempts} não deu certo. Confira a sua internet e tente de novo.`,
  });
}

/**
 * Dispara uma tentativa de reconexão e sempre termina com um resultado
 * aplicado ao estado. Chamadas concorrentes compartilham a mesma tentativa.
 */
export function requestChatReconnect(): Promise<ChatReconnectResult> {
  if (emAndamento) return emAndamento;

  const fn = reconnectHandler;
  if (!fn) {
    aplicarResultado('failed');
    patch({ message: 'O chat ainda não está pronto nesta tela. Recarregue a página.' });
    return Promise.resolve('failed');
  }

  // `since` fica como está: numa reconexão de boot ainda não houve queda, e é
  // isso que o alerta usa para não aparecer enquanto o app só está subindo.
  patch({ status: 'reconnecting', message: null });

  let timer: ReturnType<typeof setTimeout> | null = null;
  const comLimite = new Promise<ChatReconnectResult>((resolve) => {
    timer = setTimeout(() => resolve('failed'), RECONNECT_TIMEOUT_MS);
  });

  const tentativa = Promise.race([
    Promise.resolve()
      .then(fn)
      .catch(() => 'failed' as ChatReconnectResult),
    comLimite,
  ])
    .then((res) => {
      if (timer) clearTimeout(timer);
      aplicarResultado(res);
      return res;
    })
    .finally(() => {
      emAndamento = null;
    });

  emAndamento = tentativa;
  return tentativa;
}
